// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import { t } from "@lingui/macro"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import * as TrashedTimeRowSlice from "../trash/slice"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import {
  TrashStatus,
  UnTrashTimeRowsRequestSchema,
  UnTrashTimeRowsResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type UntrashTimeRowsRpc = (request: Init<typeof UnTrashTimeRowsRequestSchema>) => Observable<UnTrashTimeRowsResponse>

export const untrashTimeRows = (untrashTimeRowsRpc: UntrashTimeRowsRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.untrashTimeRows.match),
      mergeMap(action => {
        const timeRowIdsList = action.payload.timeRowIds ?? []
        return concat(
          of(
            // mark rows as being untrashed
            TimelineSlice.setUpdatingTimeRowsStatus({
              trashStatus: TrashStatus.NOT_TRASHED,
              timeRowIds: timeRowIdsList,
            })
          ),
          untrashTimeRowsRpc(action.payload).pipe(
            mergeMap(_ => concat(
              // unset trashed status for rows
              of(TimelineSlice.updateTimeRowsStatus({
                trashStatus: TrashStatus.NOT_TRASHED,
                timeRowIds: timeRowIdsList,
              })),
              // remove rows from trash page action pane
              of(TrashedTimeRowSlice.removeActiveTrashedTimeRowIds(timeRowIdsList)),
              // remove pending untrashing
              of(TimelineSlice.removeUpdatingTimeRowsStatus(timeRowIdsList)))
            ),
            catchError(e => concat(
              // unmark rows as being untrashed
              of(TimelineSlice.removeUpdatingTimeRowsStatus(timeRowIdsList)),
              showErrorNotification(e)
            )),
          ),
        )
      }),
    )

const showErrorNotification = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_untrash_time_rows_failed_permission_denied", message: "Failed to restore time rows. You do not have permission." }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_untrash_time_rows_failed_precondition", message: "Failed to restore time rows. Time rows may be out of sync." }),
          messageType: "ERROR",
        })),
        of(TrashedTimeRowSlice.setTrashedRowsRefreshRequired(true))
      )
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_untrash_time_rows_failed", message: "Failed to restore time rows. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const untrashTimeRowsRpc = (request: Init<typeof UnTrashTimeRowsRequestSchema>): Observable<UnTrashTimeRowsResponse> =>
  executorMutation(() => timelineService.unTrashTimeRows(create(UnTrashTimeRowsRequestSchema, request)))

export default untrashTimeRows(untrashTimeRowsRpc)
